import React from 'react';
import { useLocation, NavLink, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  /* AlertTriangle, */
  Bot,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  FileText,
  LogOut,
  Megaphone,
  MessageSquare,
  Newspaper,
  Percent,
  Layers,
  Sparkles,
  TrendingUp,
} from 'lucide-react';
import { getMe, logout } from '../api';
import { GEO_PAGE_TITLE, GEO_ROUTES, isPromptPulseNavActive } from '../features/geo/utils/geoConstants';
import { NavSubmenu } from './CollapsedNavSubmenu';

const ASSISTANT_ROUTE_PATHS = [
  '/assistants',
  '/deep-researcher-competitor',
  '/competitor-web-researcher',
  '/state-laws-regulation-deep-search',
  '/doi-researcher-deep-search',
  '/jobs-agent',
];

const isAssistantsNavActive = (pathname) => ASSISTANT_ROUTE_PATHS.some(
  (path) => pathname === path || pathname.startsWith(`${path}/`),
);

const SEC_FILING_ROUTE_PATHS = ['/sec-filing', '/sec-filing/insights', '/sec-filing/table'];

const isSecFilingNavActive = (pathname) => SEC_FILING_ROUTE_PATHS.some(
  (path) => pathname === path || pathname.startsWith(`${path}/`),
);

const COMING_SOON_NAV_ITEMS = [
  { label: 'Sales', icon: TrendingUp },
  { label: 'Rates', icon: Percent },
];

const MARKETING_PATHS = ['/marketing', '/marketing/linkedin', '/marketing/meta'];
const isMarketingSection = (pathname) => MARKETING_PATHS.some(
  (path) => pathname === path || pathname.startsWith(`${path}/`),
);

/** Grey parent highlight when on a child route (not black-active). Stays on when sidebar is collapsed. */
function getSubmenuParentNavClassName(isSectionActive, isSubmenuOpen, isSidebarCollapsed) {
  const highlightParent = isSectionActive && (!isSubmenuOpen || isSidebarCollapsed);
  return `nav-item${highlightParent ? ' active' : ''}`;
}

const Sidebar = ({
  isCollapsed,
  onToggle
}) => {
  const [user, setUser] = React.useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [isCompetitorsOpen, setIsCompetitorsOpen] = React.useState(true);
  const [isNexusOpen, setIsNexusOpen] = React.useState(false);
  const [isMarketingOpen, setIsMarketingOpen] = React.useState(false);
  const [isSecFilingOpen, setIsSecFilingOpen] = React.useState(false);
  const [isGeoAnalyticsOpen, setIsGeoAnalyticsOpen] = React.useState(false);
  // const [isObjectionsOpen, setIsObjectionsOpen] = React.useState(true);
  const lastFilingsPath = sessionStorage.getItem('comply_last_filings_path') || '';
  const lastFilingsTab = sessionStorage.getItem('comply_active_filings_tab') || 'Insights';
  // const lastObjectionsTab = sessionStorage.getItem('comply_active_objections_tab') || 'Insights';
  // const objectionsTabParam = new URLSearchParams(location.search).get('tab') || '';
  // const objectionsEffectiveTab =
  //   objectionsTabParam === 'Table'
  //     ? 'Table'
  //     : objectionsTabParam === 'Insights'
  //       ? 'Insights'
  //       : lastObjectionsTab === 'Table' && location.pathname === '/objections'
  //         ? 'Table'
  //         : 'Insights';
  const isFilingDetailsRoute = location.pathname.startsWith('/filing/') || location.pathname.startsWith('/filings/');
  const isCompetitorsSection = location.pathname === '/competitors' || location.pathname === '/competitors/alerts';
  const isNexusSection = location.pathname === '/nexus' || location.pathname.startsWith('/nexus/');
  const isMarketingNavSection = isMarketingSection(location.pathname);
  const isNewsPrSection = location.pathname === '/news-pr' || location.pathname.startsWith('/news-pr/');
  const lastNexusTab = sessionStorage.getItem('comply_active_nexus_tab') || 'View';
  const isSecFilingSection = isSecFilingNavActive(location.pathname);
  const isGeoAnalyticsSection = isPromptPulseNavActive(location.pathname);
  const detailsLinkedToCompetitors = isFilingDetailsRoute && (
    lastFilingsPath === '/competitors' || lastFilingsPath === '/competitors/alerts'
  );

  const closeAllSubmenus = React.useCallback(() => {
    setIsCompetitorsOpen(false);
    setIsNexusOpen(false);
    setIsMarketingOpen(false);
    setIsSecFilingOpen(false);
    setIsGeoAnalyticsOpen(false);
  }, []);

  const submenuOpenByKey = React.useMemo(() => ({
    competitors: isCompetitorsOpen,
    nexus: isNexusOpen,
    marketing: isMarketingOpen,
    secFiling: isSecFilingOpen,
    geo: isGeoAnalyticsOpen,
  }), [isCompetitorsOpen, isNexusOpen, isMarketingOpen, isSecFilingOpen, isGeoAnalyticsOpen]);

  const setSubmenuOpenByKey = React.useMemo(() => ({
    competitors: setIsCompetitorsOpen,
    nexus: setIsNexusOpen,
    marketing: setIsMarketingOpen,
    secFiling: setIsSecFilingOpen,
    geo: setIsGeoAnalyticsOpen,
  }), []);

  const handleSubmenuToggle = React.useCallback((menuKey) => {
    const isOpen = submenuOpenByKey[menuKey];
    const setOpen = setSubmenuOpenByKey[menuKey];
    if (isCollapsed) {
      closeAllSubmenus();
      if (!isOpen) {
        setOpen(true);
      }
      return;
    }
    setOpen(!isOpen);
  }, [isCollapsed, submenuOpenByKey, setSubmenuOpenByKey, closeAllSubmenus]);

  React.useEffect(() => {
    getMe().then(res => setUser(res.data.user)).catch(err => console.error('Error fetching user:', err));
  }, []);
  React.useEffect(() => {
    if (isCollapsed) {
      closeAllSubmenus();
      return;
    }

    const onCompetitors = isCompetitorsSection || detailsLinkedToCompetitors;
    setIsCompetitorsOpen(onCompetitors);
    setIsNexusOpen(isNexusSection);
    setIsMarketingOpen(isMarketingNavSection);
    setIsSecFilingOpen(isSecFilingSection);
    setIsGeoAnalyticsOpen(isGeoAnalyticsSection);
  }, [
    isCollapsed,
    isCompetitorsSection,
    detailsLinkedToCompetitors,
    isNexusSection,
    isMarketingNavSection,
    isSecFilingSection,
    isGeoAnalyticsSection,
    closeAllSubmenus,
  ]);

  const resetDashboard = () => {
    sessionStorage.removeItem('dashboard_search');
    sessionStorage.removeItem('dashboard_page');
  };

  const handleSubmenuNavigate = () => {
    resetDashboard();
    if (isCollapsed) {
      closeAllSubmenus();
    }
  };

  const handleNexusNavigate = (tab) => {
    sessionStorage.setItem('comply_active_nexus_tab', tab);
    handleSubmenuNavigate();
  };
  const handleAnswersNavigate = (event) => {
    resetDashboard();
    event.preventDefault();
    const onAnswers = location.pathname === '/answers' || location.pathname.startsWith('/answers/');
    navigate('/answers', {
      replace: onAnswers,
      state: { reset: Date.now() },
    });
  };
  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };
  return <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
    <div className="logo">
      <div className="sidebar-header">
        <div className="logo-mark">
          <div className="logo-icon">IA</div>
          {!isCollapsed && <div className="logo-text">
            <div className="logo-name">Intel Axis</div>
            <div className="logo-sub">Powered by Comply</div>
          </div>}
        </div>
        <button className="sidebar-toggle-btn" onClick={onToggle} title={isCollapsed ? "Expand" : "Collapse"}>
          {isCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </div>
    </div>

    <nav className="nav">
      {!isCollapsed && <div className="nav-section-label"></div>}

      {/* <NavLink to="/" className={`nav-item ${isDashboardActive ? 'active' : ''}`} end>
          <LayoutDashboard className="nav-icon" size={16} />
          {!isCollapsed && <span>Dashboard</span>}
         </NavLink> */}

      {/* <div className="nav-item-group">
        <div className={`nav-item ${(location.pathname === '/filings' || location.pathname === '/our-filings' || location.pathname === '/rival-filings' || location.pathname === '/all-companies' || location.pathname === '/objections') && !isDataOpen ? 'active' : ''}`} onClick={() => setIsDataOpen(!isDataOpen)}>
          <List className="nav-icon" size={16} />
          {!isCollapsed && <>
            <span className="sidebar-style-1">Data + Insights</span>
            <ChevronDown size={14} style={{
              transform: isDataOpen ? 'rotate(180deg)' : 'rotate(0deg)'
            }} className="sidebar-style-2" />
          </>}
        </div>
        {isDataOpen && <div className="nav-sub">
          <NavLink to="/our-filings" className={({
            isActive
          }) => `nav-sub-item ${isActive ? 'active' : ''}`} onClick={resetDashboard}>
            Our Filings
          </NavLink>
          <NavLink to="/rival-filings" className={({
            isActive
          }) => `nav-sub-item ${isActive ? 'active' : ''}`} onClick={resetDashboard}>
            All Filings
          </NavLink>

          <NavLink to="/all-companies" className={({
            isActive
          }) => `nav-sub-item ${isActive ? 'active' : ''}`} onClick={resetDashboard}>
            All Companies
          </NavLink>
          <NavLink to="/objections" className={({
            isActive
          }) => `nav-sub-item ${isActive ? 'active' : ''}`} onClick={resetDashboard}>
            Objections
          </NavLink>
        </div>}
      </div> */}

      <div className="nav-item-group" style={{ marginTop: '8px' }}>
        <div
          className={getSubmenuParentNavClassName(
            isCompetitorsSection || detailsLinkedToCompetitors,
            isCompetitorsOpen,
            isCollapsed,
          )}
          onClick={() => handleSubmenuToggle('competitors')}
        >
          <BarChart3 className="nav-icon" size={16} />
          {!isCollapsed && <>
            <span className="sidebar-style-1">State Insurance Filings</span>
            <ChevronDown size={14} style={{
              transform: isCompetitorsOpen ? 'rotate(180deg)' : 'rotate(0deg)'
            }} className="sidebar-style-2" />
          </>}
        </div>
        <NavSubmenu isOpen={isCompetitorsOpen} isCollapsed={isCollapsed}>
          <NavLink to="/competitors?tab=Insights" className={({
            isActive
          }) => `nav-sub-item ${(isActive && location.search.includes('tab=Insights')) || (detailsLinkedToCompetitors && lastFilingsTab === 'Insights') ? 'active' : ''}`} onClick={handleSubmenuNavigate}>
            Insights
          </NavLink>
          <NavLink
            to="/competitors/alerts"
            className={() => `nav-sub-item ${location.pathname === '/competitors/alerts' || (detailsLinkedToCompetitors && lastFilingsPath === '/competitors/alerts') ? 'active' : ''}`}
            onClick={handleSubmenuNavigate}
          >
            Alerts
          </NavLink>
          <NavLink to="/competitors?tab=Table" className={({
            isActive
          }) => `nav-sub-item ${(isActive && location.search.includes('tab=Table')) || (detailsLinkedToCompetitors && lastFilingsTab === 'Table') ? 'active' : ''}`} onClick={handleSubmenuNavigate}>
            Table
          </NavLink>
        </NavSubmenu>
      </div>

      <div className="nav-item-group" style={{ marginTop: '8px' }}>
        <div
          className={getSubmenuParentNavClassName(isNexusSection, isNexusOpen, isCollapsed)}
          onClick={() => handleSubmenuToggle('nexus')}
        >
          <Layers className="nav-icon" size={16} />
          {!isCollapsed && (
            <>
              <span className="sidebar-style-1">Nexus</span>
              <ChevronDown
                size={14}
                style={{ transform: isNexusOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                className="sidebar-style-2"
              />
            </>
          )}
        </div>
        <NavSubmenu isOpen={isNexusOpen} isCollapsed={isCollapsed}>
            <NavLink
              to="/nexus/view"
              className={() =>
                `nav-sub-item ${
                  location.pathname === '/nexus/view' ||
                  (location.pathname === '/nexus' && lastNexusTab === 'View')
                    ? 'active'
                    : ''
                }`
              }
              onClick={() => handleNexusNavigate('View')}
            >
              Canvas
            </NavLink>
            {/* <NavLink
              to="/nexus/pulse"
              className={() =>
                `nav-sub-item ${
                  location.pathname === '/nexus/pulse' || lastNexusTab === 'Pulse' || lastNexusTab === 'Table'
                    ? 'active'
                    : ''
                }`
              }
              onClick={() => handleNexusNavigate('Pulse')}
            >
              Pulse View
            </NavLink> */}
        </NavSubmenu>
      </div>

      <div className="nav-item-group" style={{ marginTop: '8px' }}>
        <div
          className={getSubmenuParentNavClassName(isMarketingNavSection, isMarketingOpen, isCollapsed)}
          onClick={() => handleSubmenuToggle('marketing')}
        >
          <Megaphone className="nav-icon" size={16} />
          {!isCollapsed && (
            <>
              <span className="sidebar-style-1">Marketing</span>
              <ChevronDown
                size={14}
                style={{ transform: isMarketingOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                className="sidebar-style-2"
              />
            </>
          )}
        </div>
        <NavSubmenu isOpen={isMarketingOpen} isCollapsed={isCollapsed}>
            <NavLink
              to="/marketing/linkedin"
              className={() =>
                `nav-sub-item ${
                  location.pathname.startsWith('/marketing/linkedin') ||
                  location.pathname === '/marketing'
                    ? 'active'
                    : ''
                }`
              }
              onClick={handleSubmenuNavigate}
            >
              LinkedIn
            </NavLink>
            <NavLink
              to="/marketing/meta"
              className={() =>
                `nav-sub-item ${location.pathname.startsWith('/marketing/meta') ? 'active' : ''}`
              }
              onClick={handleSubmenuNavigate}
            >
              Meta
            </NavLink>
            <NavLink
              to="/marketing/google"
              className={() =>
                `nav-sub-item ${location.pathname.startsWith('/marketing/google') ? 'active' : ''}`
              }
              onClick={handleSubmenuNavigate}
            >
              Google
            </NavLink>
        </NavSubmenu>
      </div>

      <NavLink
        to="/news-pr"
        className={() => `nav-item ${isNewsPrSection ? 'active black-active' : ''}`}
        style={{ marginTop: '8px' }}
        onClick={resetDashboard}
      >
        <Newspaper className="nav-icon" size={16} />
        {!isCollapsed && <span className="sidebar-style-1">News / PR</span>}
      </NavLink>

      {/* Objections — hidden for now
      <div className="nav-item-group" style={{ marginTop: '8px' }}>
        <div className={`nav-item ${location.pathname === '/objections' && !isObjectionsOpen ? 'active' : ''}`} onClick={() => setIsObjectionsOpen(!isObjectionsOpen)}>
          <AlertTriangle className="nav-icon" size={16} />
          {!isCollapsed && <>
            <span className="sidebar-style-1" style={{ fontSize: '12px' }}>Objections</span>
            <ChevronDown size={14} style={{
              transform: isObjectionsOpen ? 'rotate(180deg)' : 'rotate(0deg)'
            }} className="sidebar-style-2" />
          </>}
        </div>
        {isObjectionsOpen && <div className="nav-sub">
          <NavLink to="/objections?tab=Insights" end={false} className={() =>
            `nav-sub-item ${location.pathname === '/objections' && objectionsEffectiveTab === 'Insights' ? 'active' : ''}`
          } onClick={handleSubmenuNavigate}>
            Insights
          </NavLink>
          <NavLink to="/objections?tab=Table" end={false} className={() =>
            `nav-sub-item ${location.pathname === '/objections' && objectionsEffectiveTab === 'Table' ? 'active' : ''}`
          } onClick={handleSubmenuNavigate}>
            Table
          </NavLink>
        </div>}
      </div>
      */}

      

      <NavLink to="/answers" className={({ isActive }) => `nav-item ${isActive ? 'active black-active' : ''}`} style={{ marginTop: '8px' }} onClick={handleAnswersNavigate}>
        <MessageSquare className="nav-icon" size={16} />
        {!isCollapsed && <span className="sidebar-style-1">Answers</span>}
      </NavLink>
      <NavLink
        to="/assistants"
        className={() => `nav-item ${isAssistantsNavActive(location.pathname) ? 'active black-active' : ''}`}
        style={{ marginTop: '8px' }}
        onClick={resetDashboard}
      >
        <Bot className="nav-icon" size={16} />
        {!isCollapsed && <span className="sidebar-style-1">Assistants</span>}
      </NavLink>

      <div className="nav-item-group" style={{ marginTop: '8px' }}>
        <div
          className={getSubmenuParentNavClassName(isSecFilingSection, isSecFilingOpen, isCollapsed)}
          onClick={() => handleSubmenuToggle('secFiling')}
        >
          <FileText className="nav-icon" size={16} />
          {!isCollapsed && <>
            <span className="sidebar-style-1">SEC Filing</span>
            <ChevronDown
              size={14}
              style={{
                transform: isSecFilingOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
              className="sidebar-style-2"
            />
          </>}
        </div>
        <NavSubmenu isOpen={isSecFilingOpen} isCollapsed={isCollapsed}>
            <NavLink
              to="/sec-filing/insights"
              className={() => `nav-sub-item ${location.pathname === '/sec-filing/insights' || location.pathname === '/sec-filing' ? 'active' : ''}`}
              onClick={handleSubmenuNavigate}
            >
              Insights
            </NavLink>
            <NavLink
              to="/sec-filing/table"
              className={({ isActive }) => `nav-sub-item ${isActive ? 'active' : ''}`}
              onClick={handleSubmenuNavigate}
            >
              Table
            </NavLink>
        </NavSubmenu>
      </div>

      <div className="nav-item-group" style={{ marginTop: '8px' }}>
        <div
          className={getSubmenuParentNavClassName(isGeoAnalyticsSection, isGeoAnalyticsOpen, isCollapsed)}
          onClick={() => handleSubmenuToggle('geo')}
        >
          <Sparkles className="nav-icon" size={16} />
          {!isCollapsed && <>
            <span className="sidebar-style-1">{GEO_PAGE_TITLE}</span>
            <ChevronDown
              size={14}
              style={{
                transform: isGeoAnalyticsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
              className="sidebar-style-2"
            />
          </>}
        </div>
        <NavSubmenu isOpen={isGeoAnalyticsOpen} isCollapsed={isCollapsed}>
            <NavLink
              to={GEO_ROUTES.insights}
              className={() => `nav-sub-item ${location.pathname === GEO_ROUTES.insights || location.pathname === GEO_ROUTES.root ? 'active' : ''}`}
              onClick={handleSubmenuNavigate}
            >
              Insights
            </NavLink>
            <NavLink
              to={GEO_ROUTES.prompts}
              className={({ isActive }) => `nav-sub-item ${isActive ? 'active' : ''}`}
              onClick={handleSubmenuNavigate}
            >
              Prompts
            </NavLink>
        </NavSubmenu>
      </div>

      {COMING_SOON_NAV_ITEMS.map(({ label, icon: Icon }, index) => (
        <div
          key={label}
          className="nav-item nav-item--disabled"
          style={{ marginTop: index === 0 ? '8px' : undefined }}
          aria-disabled="true"
          title={label}
        >
          <Icon className="nav-icon" size={16} aria-hidden="true" />
          {!isCollapsed && <span className="sidebar-style-1">{label}</span>}
        </div>
      ))}

      {/* <NavLink to="/analytics" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} onClick={resetDashboard}>
          <BarChart3 className="nav-icon" size={16} />
          {!isCollapsed && <span>Analytics</span>}
         </NavLink> */}

      {/* <NavLink to="/changelog" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} onClick={resetDashboard}>
          <History className="nav-icon" size={16} />
          Change log
         </NavLink>
          <NavLink to="/objections" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} onClick={resetDashboard}>
          <AlertTriangle className="nav-icon" size={16} />
          Objections
          <span className="nav-badge">8</span>
         </NavLink>
          <NavLink to="/documents" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} onClick={resetDashboard}>
          <FileText className="nav-icon" size={16} />
          Documents
         </NavLink>
          <div className="nav-section-label" style={{ marginTop: '8px' }}>Automation</div>
          <NavLink to="/scheduler" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} onClick={resetDashboard}>
          <Calendar className="nav-icon" size={16} />
          Scheduler
         </NavLink>
          <NavLink to="/alerts" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} onClick={resetDashboard}>
          <Mail className="nav-icon" size={16} />
          Email alerts
          <span className="nav-badge amber">3</span>
         </NavLink>
          <NavLink to="/run-history" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} onClick={resetDashboard}>
          <Clock className="nav-icon" size={16} />
          Run history
         </NavLink>
          <div className="nav-section-label" style={{ marginTop: '8px' }}>Settings</div>
          <NavLink to="/filters" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} onClick={resetDashboard}>
          <Settings className="nav-icon" size={16} />
          States & filters
         </NavLink> */}
    </nav>

    <div className="sidebar-footer">

      {!isCollapsed && (
        <div className="sf-user">
          <div className="sf-label">Logged in as:</div>
          <div className="sf-time">{user?.name || 'Loading...'}</div>
          {/* <div className="sf-status">{user?.client?.client_name || '...'}</div> */}
        </div>
      )}
      <button
        type="button"
        className={`sf-logout${isCollapsed ? ' sf-logout--collapsed' : ''}`}
        onClick={handleLogout}
        title="Sign out"
        aria-label="Sign out"
      >
        <LogOut size={isCollapsed ? 16 : 13} />
        {!isCollapsed && <span>Sign out</span>}
      </button>
    </div>
  </div>;
};
export default Sidebar;